import { useCallback, useState } from 'react';
import { ERROR_MESSAGES, toUserMessage } from '../../utils/userErrors';
import apiService from '../../utils/apiService';
import { useFetchOnceByKey } from '../../hooks/useMountOnce';
import { parseUserProfile } from '../../utils/userProfile';
import {
  buildTierHistory,
  getNextTier,
  matchCurrentTier,
  parseAccountTierProgress,
  parseTierLevels,
  parseUserTier,
} from './tierHelpers';

/**
 * My Progress: GET /Affiliate/GetAccountTierProgress only.
 * Do not call GetUserLevels / GetPartnersLevels here.
 */
export const useAccountTierProgress = (fetchKey = 'at-my-progress') => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userTier, setUserTier] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [history, setHistory] = useState([]);
  const [currentTier, setCurrentTier] = useState(null);
  const [nextTier, setNextTier] = useState(null);

  const refresh = useCallback(async ({ cancelled }) => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.getAccountTierProgress().catch(() => ({
        success: false,
        data: null,
        error: 'Failed to load account tier progress',
      }));
      if (cancelled()) return;

      const parsed = parseAccountTierProgress(res?.success ? res.data : null);

      setTiers(parsed.tiers);
      setUserTier(parsed.userTier);
      setCurrentTier(parsed.currentTier);
      setNextTier(parsed.nextTier);
      setHistory(parsed.history);

      if (!res?.success) {
        const msg = res?.error || 'Failed to load account tier progress';
        if (!String(msg).includes('404')) {
          setError(typeof msg === 'string' ? msg : 'Failed to load account tier progress');
        }
      }
    } catch (err) {
      if (!cancelled()) setError(toUserMessage(err, ERROR_MESSAGES.load));
    } finally {
      if (!cancelled()) setLoading(false);
    }
  }, []);

  useFetchOnceByKey(fetchKey, refresh, true);

  return {
    loading,
    error,
    userTier,
    tiers,
    history,
    currentTier,
    nextTier,
    refresh,
  };
};

/** Legacy: GetUserLevels + GetPartnersLevels (e.g. Instruments Commission). */
export const useAccountTier = (fetchKey = 'account-tier') => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userTier, setUserTier] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [history, setHistory] = useState([]);
  const [currentTier, setCurrentTier] = useState(null);
  const [nextTier, setNextTier] = useState(null);

  const refresh = useCallback(async ({ cancelled }) => {
    setLoading(true);
    setError('');
    try {
      const [levelRes, partnersRes, profileRes] = await Promise.all([
        apiService.getUserLevels().catch(() => ({ success: false, data: null })),
        apiService.getPartnersLevels().catch(() => ({ success: false, data: null })),
        apiService.getUserProfile().catch(() => ({ success: false, data: null })),
      ]);
      if (cancelled()) return;

      const profile = profileRes?.success ? parseUserProfile(profileRes.data) : {};
      const tierList = parseTierLevels(partnersRes);
      const user = parseUserTier(levelRes?.success ? levelRes.data : null, profile);
      const current = matchCurrentTier(tierList, user);
      const next = getNextTier(tierList, current, user);

      if (current && !user.commissionPerLot) {
        user.commissionPerLot = current.commissionPerLot;
      }
      if (next && !user.nextLevelName) {
        user.nextLevelName = next.name;
      }
      if (next && !(user.targetPoints > 0)) {
        user.targetPoints = next.points;
        user.nextLevelPoints = next.points;
        user.pointsToNext = Math.max(0, next.points - user.currentPoints);
        user.progressPct =
          next.points > 0 ? Math.min(100, Math.round((user.currentPoints / next.points) * 100)) : 0;
      } else if (user.targetPoints > 0) {
        user.progressPct = Math.min(
          100,
          Math.round((user.currentPoints / user.targetPoints) * 100)
        );
      }

      setTiers(tierList);
      setUserTier(user);
      setCurrentTier(current);
      setNextTier(next);
      setHistory(buildTierHistory(user, tierList));

      if (!levelRes.success && !partnersRes.success) {
        const msg = levelRes.error || partnersRes.error || 'Failed to load account tier';
        if (!String(msg).includes('404')) setError(typeof msg === 'string' ? msg : 'Failed to load account tier');
      }
    } catch (err) {
      if (!cancelled()) setError(toUserMessage(err, ERROR_MESSAGES.load));
    } finally {
      if (!cancelled()) setLoading(false);
    }
  }, []);

  useFetchOnceByKey(fetchKey, refresh, true);

  return {
    loading,
    error,
    userTier,
    tiers,
    history,
    currentTier,
    nextTier,
    refresh,
  };
};

export default useAccountTier;
